import Text from 'components/shared/Text'
import Box from 'components/shared/Box'
import { SummaryItem } from './SummaryItem'
import { SummaryBoxWrapper } from 'components/global/Summary/styles'
import SeparatedList from 'components/shared/SeparatedList'
import { getDayNameAndDate } from 'helpers/dates'
import { FormattedMessage } from 'react-intl'
import {
    CreateReservationRoomDto,
    GetCartItemPresenter,
} from 'store/api-slice/hooks'
import Link from 'components/shared/Link'
import { Step, UnlimitedCardStep } from 'store/general-slice/types'
import { useApp } from 'store/steps'
import { setIsMobileSummaryOpened } from 'store/general-slice'
import { useAppDispatch } from 'store/hooks'
import { toggleBodyScroll } from 'helpers/helpers'
import { useResolutions } from 'store/resolutions'
import { SummaryItemAccommodationPromotion } from 'components/global/Summary/sections/SummaryItemAccommodationPromotion'
import { PRODUCT_TYPE_SPA } from 'constants/app'

interface SummaryBoxProps {
    title: string
    dates?: string[]
    items: GetCartItemPresenter[]
    stepToEdit: Step | UnlimitedCardStep
    hasPromotion?: boolean
    totalPrice?: number
    totalPriceWithoutDiscount?: number
    rooms?: CreateReservationRoomDto[]
    shouldCountNights?: boolean
}

export const SummaryBox = ({
    title,
    dates,
    items,
    stepToEdit,
    hasPromotion = false,
    totalPrice,
    rooms,
    totalPriceWithoutDiscount,
    shouldCountNights,
}: SummaryBoxProps) => {
    const { stepId, goBackToStep } = useApp()
    const dispatch = useAppDispatch()
    const { isTablet } = useResolutions()
    const startDate = dates && `${getDayNameAndDate({ date: dates[0] })}`
    const endDate =
        dates && `${getDayNameAndDate({ date: dates[dates.length - 1] })}`

    const handleEdit = () => {
        if (isTablet) {
            dispatch(setIsMobileSummaryOpened(false))
            toggleBodyScroll()
        }

        goBackToStep(stepToEdit)
    }

    const isItemAccommodationWithPromotion =
        stepToEdit === 'accommodation' && hasPromotion

    return (
        <SummaryBoxWrapper>
            <Box direction="row" isFullWidth justify="space-between">
                <Text variant="heading5" mb={16} color="secondary_500">
                    {title}
                </Text>
                {stepId !== stepToEdit && (
                    <Link
                        variant="gray"
                        icon={null}
                        onClick={handleEdit}
                        data-testid={`cart-backto-edit-${stepToEdit}`}
                    >
                        <FormattedMessage
                            id="global.btn.edit"
                            defaultMessage="Edytuj"
                        />
                    </Link>
                )}
            </Box>
            {dates && (
                <Box gap={4} mb={12} direction={'row'}>
                    <Text variant="bodySmall" color="grayscale_900">
                        {startDate} {dates.length > 1 && `- ${endDate}`}
                    </Text>
                    {dates.length > 1 && (
                        <Text variant="bodySmall" color="grayscale_700">
                            (
                            {shouldCountNights ? (
                                <FormattedMessage
                                    id="global.plurals.nights"
                                    defaultMessage="{nights, plural, one {# noc} few {# noce} many {# nocy} other {# nocy}}"
                                    values={{
                                        nights: dates.length - 1,
                                    }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="global.plurals.days"
                                    defaultMessage="{days, plural, few {# dni} many {# dni} other {# dnia}}"
                                    values={{
                                        days: dates.length,
                                    }}
                                />
                            )}
                            )
                        </Text>
                    )}
                </Box>
            )}
            {isItemAccommodationWithPromotion && rooms ? (
                <SummaryItemAccommodationPromotion
                    items={items}
                    totalPrice={totalPrice || 0}
                    totalPriceWithoutDiscount={totalPriceWithoutDiscount || 0}
                    rooms={rooms}
                    hasPromotion={hasPromotion}
                />
            ) : (
                <SeparatedList gap={12} direction="column">
                    {items.map((item, index) => {
                        const {
                            name,
                            quantity,
                            unitPrice,
                            cartItemId,
                            description,
                            hasPromotion,
                            totalPriceWithoutDiscount,
                            totalPrice,
                            productType,
                        } = item

                        if (!name) return null

                        let totalPriceForItem = totalPrice || 0
                        let totalPriceWithoutDiscountForItem =
                            totalPriceWithoutDiscount || 0

                        if (
                            stepToEdit === 'upsell' &&
                            totalPrice &&
                            quantity &&
                            totalPriceWithoutDiscount
                        ) {
                            totalPriceForItem = totalPrice / quantity
                            totalPriceWithoutDiscountForItem =
                                totalPriceWithoutDiscount / quantity
                        }

                        const summaryItemQuantity = rooms
                            ? rooms[index].quantity
                            : quantity

                        return (
                            <SummaryItem
                                name={name}
                                description={description}
                                price={unitPrice || 0}
                                totalPrice={totalPriceForItem}
                                quantity={summaryItemQuantity || 0}
                                totalPriceWithoutDiscount={
                                    totalPriceWithoutDiscountForItem
                                }
                                hasPromotion={!!hasPromotion}
                                isTruncable={productType === PRODUCT_TYPE_SPA}
                                key={`${cartItemId}-${index}`}
                            />
                        )
                    })}
                </SeparatedList>
            )}
        </SummaryBoxWrapper>
    )
}
